
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { ShieldAlert, ArrowLeft, Home, LockKeyhole, AlertCircle, User, Code } from 'lucide-react';

export default function Unauthorized() {
  const {
    isAuthenticated,
    userRole,
    isAdmin
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDebug, setShowDebug] = useState(false);
  
  const message = (location.state as any)?.message || "Sie haben keine Berechtigung, auf diese Seite zuzugreifen.";
  const requiredRoles = (location.state as any)?.requiredRoles || [];
  const currentUrl = (location.state as any)?.currentUrl || '';

  useEffect(() => {
    // If user is authenticated, update page title
    if (isAuthenticated) {
      document.title = "Keine Berechtigung | CHRONA";
    }
  }, [isAuthenticated]);

  return (
    <motion.div 
      initial={{
        opacity: 0,
        y: 20
      }} 
      animate={{
        opacity: 1,
        y: 0
      }} 
      transition={{
        duration: 0.5
      }} 
      className="container flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4 py-16"
    >
      <div className="p-6 max-w-md w-full bg-chrona-gray-800/40 rounded-xl border border-chrona-gray-700/50 shadow-xl backdrop-blur-lg">
        <div className="flex flex-col items-center text-center">
          <div className="p-3 bg-red-500/10 rounded-full mb-4">
            <ShieldAlert className="h-12 w-12 text-red-400" />
          </div>
          
          <h1 className="text-2xl font-bold mb-2">Zugriff verweigert</h1>
          
          <p className="text-chrona-gray-300 mb-6">{message}</p>
          
          {isAuthenticated && (
            <div className="mb-6 p-4 rounded-lg bg-chrona-gray-700/30 border border-chrona-gray-700/50 w-full">
              <div className="flex items-center gap-2 mb-2">
                <LockKeyhole className="h-4 w-4 text-chrona-gray-400" />
                <h3 className="font-medium">Authentifizierungsstatus</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-chrona-gray-400">
                <User className="h-4 w-4" />
                <p>
                  Sie sind angemeldet als: <span className="font-semibold text-chrona-accent">{userRole || 'Unbekannte Rolle'}</span>
                </p>
              </div>
              {requiredRoles && requiredRoles.length > 0 && (
                <div className="mt-3 pt-3 border-t border-chrona-gray-700/30">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-chrona-gray-400" />
                    <p className="text-sm text-chrona-gray-400">
                      Benötigte Rolle: <span className="font-semibold text-chrona-gray-200">{requiredRoles.join(', ')}</span>
                    </p>
                  </div>
                </div>
              )}
              
              {/* Debug section for admins */}
              {isAdmin && (
                <div className="mt-3 pt-3 border-t border-chrona-gray-700/30">
                  <button 
                    onClick={() => setShowDebug(!showDebug)}
                    className="flex items-center gap-2 text-xs text-chrona-accent hover:text-chrona-accent-light"
                  >
                    <Code size={14} />
                    {showDebug ? 'Debug-Info ausblenden' : 'Debug-Info anzeigen'}
                  </button>
                  
                  {showDebug && (
                    <div className="mt-2 p-2 bg-chrona-gray-800/50 rounded text-left text-xs font-mono overflow-auto max-h-32">
                      <p>User Role: {userRole}</p>
                      <p>Required Roles: {JSON.stringify(requiredRoles)}</p>
                      <p>Current URL: {currentUrl}</p>
                      <p>State: {JSON.stringify(location.state, null, 2)}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button 
              onClick={() => navigate(-1)} 
              variant="outline" 
              className="flex-1 gap-2 bg-inherit hover:bg-chrona-gray-700/50"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </Button>
            
            <Button 
              onClick={() => navigate('/')} 
              className="flex-1 gap-2 bg-chrona-accent hover:bg-chrona-accent-light"
            >
              <Home className="h-4 w-4" />
              Zur Startseite
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
